/**
 * Unified Use-flow Step 0 — Blueprint reveal.
 * Split data: list TemplateDto → instant process map;
 * getPreview → phase/task telemetry + lockPolicy (quiet Principle 2).
 *
 * TEMPLATE-CENTER-REDESIGN-1: no gate-count badges (PLATFORM-TRUTH-SURVEY-1 —
 * armed-gate claims are false in LEAN workspaces). No Getting started footer.
 */
import { Layers, ListTodo, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import type { PreviewResponse } from '../api';
import type { TemplateDto } from '../templates.api';
import { isTemplateOriginMetadata } from '../template-origin';
import {
  deriveSetupLevel,
  templatePhaseList,
} from '../template.mapper';
import { TemplateProcessMap } from './TemplateProcessMap';
import { TemplateBlueprint } from './TemplateBlueprint';
import { PHASE5_1_COPY } from '@/constants/phase5_1.copy';
import { ORG_TEMPLATE_LABEL } from '@/features/templates/templateCenterCopy';

function bestForLine(template: TemplateDto | null | undefined, methodology?: string): string {
  if (!template) {
    return methodology
      ? `Teams using a ${methodology} delivery rhythm for structured project work.`
      : 'Teams that want a structured Zephix project with phases and starter tasks.';
  }
  const cat = template.category?.trim();
  const meth = methodology || template.methodology;
  if (cat && meth) return `${cat} workstreams using ${meth} execution.`;
  if (cat) return `${cat} programs and projects.`;
  if (meth) return `Teams standardizing on ${meth} delivery.`;
  return 'Teams that need a clear phase model and structured execution in Zephix.';
}

function humanizeLockedItem(key: string): string {
  switch (key) {
    case 'phaseOrder':
      return 'Phase order';
    case 'phaseCount':
      return 'Phase count';
    case 'reportingKeys':
      return 'Reporting keys';
    default:
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
  }
}

function lockPolicyDescription(preview: PreviewResponse): string {
  if (preview.lockPolicy.structureLocksOnStart) {
    return `${PHASE5_1_COPY.STRUCTURE_LOCKS}. Progression requires explicit phase-gate sign-off where gates are armed.`;
  }
  return 'Structure remains editable after work starts for this template.';
}

export interface TemplateBlueprintStepProps {
  template: TemplateDto;
  previewLoading: boolean;
  previewError: { code: string; message: string } | null;
  previewData: PreviewResponse | null;
}

export function TemplateBlueprintStep({
  template,
  previewLoading,
  previewError,
  previewData,
}: TemplateBlueprintStepProps) {
  const methodology = template.methodology;
  const setupLevel = deriveSetupLevel(template);
  const origin = isTemplateOriginMetadata(template.metadata) ? template.metadata : null;

  const listPhaseCount = templatePhaseList(template).length;
  const listTaskCount = (
    (template.taskTemplates ?? template.task_templates ?? []) as unknown[]
  ).length;

  const phaseCount = previewData?.phaseCount ?? listPhaseCount;
  const taskCount = previewData?.taskCount ?? listTaskCount;

  return (
    <div className="space-y-3" data-testid="use-template-step-blueprint">
      <div
        className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
        data-testid="template-preview-summary"
      >
        <div className="h-28 w-full border-b border-slate-200 bg-slate-50">
          <TemplateBlueprint template={template} size="card" className="h-full w-full" />
        </div>

        <div className="space-y-3 p-4">
          {/* Density strip — phases + tasks only (no gate counts) */}
          <div
            className="flex flex-wrap items-center gap-x-0 gap-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
            data-testid="template-blueprint-density-strip"
            aria-busy={previewLoading}
          >
            <DensityStat
              icon={<Layers className="h-3.5 w-3.5" aria-hidden />}
              label="Phases"
              value={phaseCount}
              testId="template-blueprint-stat-phases"
            />
            <span className="mx-2 hidden h-4 w-px bg-slate-200 sm:block" aria-hidden />
            <DensityStat
              icon={<ListTodo className="h-3.5 w-3.5" aria-hidden />}
              label="Tasks"
              value={taskCount}
              testId="template-blueprint-stat-tasks"
            />
            {previewLoading ? (
              <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-slate-400">
                Refreshing…
              </span>
            ) : null}
          </div>

          <p
            className="text-xs leading-relaxed text-slate-600"
            data-testid="template-preview-summary-line"
          >
            {template.description?.trim() ||
              (previewData
                ? `Structured project with ${previewData.phaseCount} phase${previewData.phaseCount === 1 ? '' : 's'} and ${previewData.taskCount} starter task${previewData.taskCount === 1 ? '' : 's'}.`
                : 'Structured project starter from this template.')}
          </p>

          <div
            className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600"
            data-testid="template-preview-attribution"
          >
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
              {template.templateScope === 'SYSTEM'
                ? 'Created by Zephix'
                : template.templateScope === 'WORKSPACE'
                  ? 'Workspace template'
                  : template.templateScope === 'ORG'
                    ? ORG_TEMPLATE_LABEL
                    : template.templateScope}
            </span>
            {methodology && (
              <span
                className="rounded-full bg-blue-50 px-2 py-0.5 font-medium capitalize text-blue-800"
                data-testid="template-preview-methodology-badge"
              >
                {methodology}
              </span>
            )}
            {setupLevel && (
              <span
                className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-medium text-slate-700"
                data-testid="template-preview-setup-badge"
              >
                {setupLevel}
              </span>
            )}
            {origin?.sourceProjectName && (
              <span className="text-slate-500">
                From project:{' '}
                <span className="font-medium text-slate-800">{origin.sourceProjectName}</span>
              </span>
            )}
          </div>

          {previewError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{previewError.message}</p>
            </div>
          )}

          <div className="rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2.5">
            <TemplateProcessMap template={template} />
          </div>

          {previewData ? (
            <div
              className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5"
              data-testid="template-blueprint-lock-policy"
            >
              <div className="flex items-start gap-2">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800">
                    {lockPolicyDescription(previewData)}
                  </p>
                  {previewData.lockPolicy.lockedItems.length > 0 ? (
                    <p className="mt-1 text-xs text-slate-500">
                      Locked after start:{' '}
                      <span className="font-mono text-slate-600">
                        {previewData.lockPolicy.lockedItems.map(humanizeLockedItem).join(' · ')}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div data-testid="template-preview-best-for">
            <h3 className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Best for
            </h3>
            <p className="text-xs text-slate-700">{bestForLine(template, methodology)}</p>
          </div>

          <p
            className="border-t border-slate-100 pt-2 text-[11px] text-slate-500"
            data-testid="template-preview-governance-note"
          >
            Defaults from this template apply on create. Name, dates, and optional settings follow
            in the next steps.
          </p>
        </div>
      </div>
    </div>
  );
}

function DensityStat({
  icon,
  label,
  value,
  testId,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  testId: string;
}) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <span className="text-slate-500">{icon}</span>
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <span className="text-sm font-semibold tabular-nums text-slate-900">{value}</span>
    </div>
  );
}
